import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { Notification } from '../../../../../../common/Atoms/Notification';
import { SmallSpinner } from '../../../../../../common/Atoms/SmallSpinner';
import { Paragraph } from '../../../../../../common/Atoms/Typography/Paragraph';
import { env } from '../../../../../../common/lib/env';
import { orgHeader } from '../../../../../../common/lib/axiosBaseQuery';
import { useApiRequest } from '../../../../../hooks/useApiRequest';
import { ContactSearch, IPickedContact } from '../../../../Common/ContactSearch';

// The PDF renders A4 at 0.7 scale with 1cm margins, so its content area is
// (210mm - 2cm) / 0.7 wide and (297mm - 2cm) / 0.7 tall in CSS px
const PDF_CONTENT_WIDTH_PX = 1026;
const PDF_CONTENT_HEIGHT_PX = 1496;

interface BuilderPreviewProps {
  html: string;
  loading: boolean;
  organisationId: string;
  templateId: number;
  /** Preview with this contact's real outstanding statement; sample data when unset */
  contact: IPickedContact | undefined;
  onContactChange: (contact: IPickedContact | undefined) => void;
}

export function BuilderPreview({
  html,
  loading,
  organisationId,
  templateId,
  contact,
  onContactChange,
}: BuilderPreviewProps) {
  const [downloading, setDownloading] = useState(false);
  const request = useApiRequest();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Lay the preview out at the PDF's width, then shrink it to fit
  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const observer = new ResizeObserver(() => setScale(Math.min(1, container.clientWidth / PDF_CONTENT_WIDTH_PX)));

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  async function onDownload() {
    if (downloading) return;
    setDownloading(true);

    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.baseApiUrl}statement-builder/templates/${templateId}/preview/pdf`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: contact ? { contactId: contact.id } : undefined,
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = url;
      link.setAttribute(`download`, `ai_statement_preview.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    catch (e) {
      const responseObj = await e.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not create statement.`}`);
    }

    setDownloading(false);
  }

  return (
    <div className={ `flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white` }>
      <div className={ `flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-4 py-3` }>
        <Paragraph
          size={ `xs` }
          variant={ `secondary` }
          className={ `uppercase tracking-wide` }
        >
          { contact ? `Preview - ${contact.name}` : `Preview - sample data` }
        </Paragraph>
        <div className={ `relative w-72` }>
          <ContactSearch
            label={ `Preview with a contact` }
            selected={ contact }
            onSelect={ onContactChange }
            compact
          />
        </div>
      </div>

      <div className={ `relative flex-1 overflow-auto bg-slate-100 p-6` }>
        <div ref={ containerRef }>
          <div
            className={ `mx-auto box-content overflow-hidden rounded-sm border border-slate-300 bg-white shadow-[0_20px_35px_rgba(15,23,42,0.15)]` }
            style={ {
              width: PDF_CONTENT_WIDTH_PX * scale,
              height: PDF_CONTENT_HEIGHT_PX * scale,
            } }
          >
            { /* AI-generated html: empty sandbox = no scripts, opaque origin */ }
            <iframe
              title={ `Statement Builder Preview` }
              srcDoc={ html }
              sandbox={ `` }
              className={ `bg-white` }
              style={ {
                width: PDF_CONTENT_WIDTH_PX,
                height: PDF_CONTENT_HEIGHT_PX,
                transform: `scale(${scale})`,
                transformOrigin: `top left`,
              } }
            />
          </div>
        </div>

        {
          loading &&
          <div className={ `absolute inset-0 flex items-center justify-center bg-slate-100/70` }>
            <div className={ `flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm` }>
              <SmallSpinner colored />
              <Paragraph size={ `xs` }>
                { `Refreshing preview...` }
              </Paragraph>
            </div>
          </div>
        }
      </div>

      <div className={ `border-t border-slate-200 p-3` }>
        <Notification type={ `info` }>
          <Paragraph as={ `span` }>
            { `Exact spacings and sizing may differ slightly on the PDF. Click ` }
          </Paragraph>
          <Paragraph
            variant={ `link` }
            as={ `span` }
          >
            <a
              className={ `cursor-pointer` }
              onClick={ event => {
                event.preventDefault();
                onDownload();
              } }
            >
              { `here` }
            </a>
          </Paragraph>
          <Paragraph as={ `span` }>
            { ` to download the PDF.` }
          </Paragraph>

          {
            downloading &&
            <Paragraph
              as={ `span` }
              className={ `ml-2` }
            >
              { `Downloading...` }
            </Paragraph>
          }
        </Notification>
      </div>
    </div>
  );
}
